import { includes, uniq } from "lodash";
import { isBefore, parse } from "date-fns";
import { context, data } from "../typescript/interfaces";
import { IndicationApprovalStageType } from "../typescript/enums/data";
import config from "../config";

export const uniqueResults = (data: context.Data[], objectKey: string): string[] => {
  const matches = data
    .map((row) => {
      if (typeof row[objectKey] === "string") {
        return row[objectKey];
      }
      return row[objectKey].map((i) => i.name);
    })
    .flat();

  const uniqueValues = uniq(matches);

  return uniqueValues;
};

export const tableData = (filters: context.Filters, data: context.Data[]): context.Data[] => {
  const filterSectionsApplied = Object.keys(filters).filter((section) => filters[section].length > 0);
  return data.filter((row) => {
    return filterSectionsApplied.every((sectionApplied) => {
      switch (sectionApplied) {
        case "hasDrugPipelineReport":
        case "hasHighImpactTag":
        case "isComplexMolecule":
          return booleanSection(row, sectionApplied);
        case "dateFrom":
        case "dateTo":
          return dateSection(filters, row, sectionApplied);
        case "brandCompanies":
        case "diseaseState":
        case "routesOfAdministration":
        case "mechanismsOfAction":
        case "otherAttributes":
        case "therapeuticAreaTags":
          return idNameSection(filters, row, sectionApplied);
        case "stages":
          return stagesSection(filters, row);
        case "complexForms":
          return complexFormsSection(filters, row);
        default:
          return defaultSection(filters, row, sectionApplied);
      }
    });
  });
};

const booleanSection = (row: context.Data, sectionApplied: string) => row[sectionApplied];

const dateSection = (filters: context.Filters, row: context.Data, sectionApplied: string) => {
  const extractedDateStr = filters[sectionApplied][0].split(": ")[1];
  let parsedDate;

  if (extractedDateStr) {
    parsedDate = Date.parse(extractedDateStr);
  }

  return row.indicationApprovalStatus.some((status) => {
    if (!status.date) return false;
    const rowDate = parse(status.date, "MM/yyyy", new Date());
    return sectionApplied === "dateFrom" ? isBefore(parsedDate, rowDate) : isBefore(rowDate, parsedDate);
  });
};

const idNameSection = (filters: context.Filters, row: context.Data, sectionApplied: string) => {
  return filters[sectionApplied].some((filterItem) => {
    return row[sectionApplied].some((item) => item.name === filterItem);
  });
};

const stagesSection = (filters: context.Filters, row: context.Data) => {
  return filters["stages"].some((filterItem) => {
    if (filterItem === "Upcoming PDUFA Date") {
      return row["indicationApprovalStatus"].some((ias) => ias.stage === IndicationApprovalStageType.Pending);
    } else if (filterItem === "Complete Response Letter") {
      return row["indicationApprovalStatus"].some((ias) => ias.stage === IndicationApprovalStageType.CRL);
    }
    return row["indicationApprovalStatus"].some((ias) => ias.stage === filterItem);
  });
};

const complexFormsSection = (filters: context.Filters, row: context.Data) => {
  const forms = row.complexForms.concat(config.featureToggles.likelyComplexFormReleased ? row.likelyComplexForms : []);
  return filters["complexForms"].some((filterItem) => forms.some((cm) => cm.name === filterItem));
};

const defaultSection = (filters: context.Filters, row: context.Data, sectionApplied: string) => {
  return filters[sectionApplied].some((filterItem) => {
    return row[sectionApplied].includes(filterItem);
  });
};

export const tableColumn = (quickFilter: string, rowData: context.Data, keys: string[]) =>
  keys.some((key) => {
    switch (key) {
      case "brandName":
      case "genericName":
        return simpleFilter(rowData[key], quickFilter);
      case "brandCompanies":
      case "routesOfAdministration":
      case "mechanismsOfAction":
        return idNameFilter(rowData[key], quickFilter);
      case "indicationApprovalStatus":
        return indicationsFilters(rowData[key], quickFilter);
      default:
        return false;
    }
  });

const simpleFilter = (rowData: string, quickFilter: string): boolean =>
  includes(rowData.toLowerCase(), quickFilter.toLowerCase());

const idNameFilter = (rowData: data.IdName[], quickFilter: string): boolean =>
  rowData.some((row) => includes(row.name.toLowerCase(), quickFilter.toLowerCase()));

const indicationsFilters = (rowData: data.IndicationApprovalStatus[], quickFilter: string) =>
  rowData.some((row) => includes(row.indication.toLowerCase(), quickFilter.toLowerCase()));

export const newArray = (arr: number[], num: number) => {
  if (arr.includes(num)) {
    return arr.filter((i) => i !== num);
  } else {
    return [...arr, num];
  }
};
